/**
 * DecksPage - Main deck list view
 *
 * Displays all user decks with filtering by status, search, and sort options.
 * Allows creating new decks and navigating to deck editor.
 */
import { useCallback, useMemo, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import { FaFileExport, FaFileImport, FaPlus, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router';

import Button from '@/components/Button/Button';
import EmptyState from '@/components/EmptyState/EmptyState';
import SortBySelector from '@/components/SortBySelector/SortBySelector';
import Spinner from '@/components/Spinner/Spinner';
import Tabs from '@/components/Tabs/Tabs';
import useCardLookup from '@/hooks/useCardLookup';
import { getDisplayImageUris } from '@/lib/cardUtils';
import { DECK_FORMAT_LABELS } from '@/lib/deckUtils';
import type { DeckFormat } from '@/types/deck';
import CreateDeckModal from './components/CreateDeckModal';
import DeckCard from './components/DeckCard';
import ExportAllDecksModal from './components/ExportAllDecksModal';
import type { DeckFormData, DeckImportPreviewCard } from './components/ImportDeckModal';
import ImportDeckModal from './components/ImportDeckModal';
import { useAddDeckCardsMutation, useCreateDeckMutation, useDecksQuery } from './hooks/api';
import { deckColorIdentitiesAtom } from './state/allDeckCards';
import { deckListFiltersAtom, type DeckListSort, type DeckListView } from './state/decksAtom';

// ============================================================================
// Types
// ============================================================================

const VIEW_OPTIONS: { value: DeckListView; label: string }[] = [
  { value: 'all', label: 'All Decks' },
  { value: 'brewing', label: 'Brewing' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

const SORT_OPTIONS: { value: DeckListSort; label: string }[] = [
  { value: 'updated', label: 'Recently Updated' },
  { value: 'name', label: 'Name' },
  { value: 'format', label: 'Format' },
  { value: 'card_count', label: 'Card Count' },
];

// ============================================================================
// Component
// ============================================================================

export default function DecksPage() {
  const { data: decks, isLoading, error } = useDecksQuery();
  const [filters, setFilters] = useAtom(deckListFiltersAtom);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const navigate = useNavigate();
  const createDeckMutation = useCreateDeckMutation();
  const { getCard, getFullCard, prefetchCards } = useCardLookup();
  const deckColorIdentities = useAtomValue(deckColorIdentitiesAtom);
  const addCardsMutation = useAddDeckCardsMutation();
  const [isPreparingDeck, setIsPreparingDeck] = useState(false);

  // Handle deck creation from import preview
  const handleCreateDeck = useCallback(
    async (deckData: DeckFormData, cards: DeckImportPreviewCard[]) => {
      setIsPreparingDeck(true);
      try {
        // Create the deck with user-provided metadata
        const deck = await createDeckMutation.mutateAsync({
          name: deckData.name,
          format: deckData.format,
          status: deckData.status,
          description: deckData.description,
        });

        // Add matched cards to deck (only cards that were found in database)
        const cardsToAdd = cards
          .filter((c) => c.matched !== null)
          .map((c) => ({
            scryfall_id: c.matched!.id,
            quantity: c.quantity,
            zone: c.zone,
          }));

        if (cardsToAdd.length > 0) {
          await addCardsMutation.mutateAsync({ deckId: deck.id, cards: cardsToAdd });

          // Prefetch full card data so deck editor renders all cards immediately
          const scryfallIds = cardsToAdd.map((c) => c.scryfall_id);
          await prefetchCards(scryfallIds);
        }

        // Navigate to the new deck
        navigate(`/decks/${deck.id}`);
      } finally {
        setIsPreparingDeck(false);
      }
    },
    [createDeckMutation, addCardsMutation, prefetchCards, navigate],
  );

  // Compute available formats from decks
  const availableFormats = useMemo(() => {
    if (!decks) return [];
    const formats = new Set<DeckFormat>();
    decks.forEach((deck) => {
      if (deck.format) formats.add(deck.format);
    });
    return Array.from(formats).sort((a, b) => DECK_FORMAT_LABELS[a].localeCompare(DECK_FORMAT_LABELS[b]));
  }, [decks]);

  // Filter and sort decks
  const filteredDecks = useMemo(() => {
    if (!decks) return [];

    let result = [...decks];

    // Filter by status
    if (filters.view !== 'all') {
      result = result.filter((d) => d.status === filters.view);
    }

    // Filter by format
    if (filters.format !== 'all') {
      result = result.filter((d) => d.format === filters.format);
    }

    // Filter by search
    if (filters.search.trim()) {
      const search = filters.search.toLowerCase();
      result = result.filter(
        (d) => d.name.toLowerCase().includes(search) || (d.format && DECK_FORMAT_LABELS[d.format].toLowerCase().includes(search)),
      );
    }

    // Sort
    result.sort((a, b) => {
      const dir = filters.sortDirection === 'asc' ? 1 : -1;
      switch (filters.sort) {
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'format':
          return (a.format ?? 'zzz').localeCompare(b.format ?? 'zzz') * dir;
        case 'card_count':
          return (a.card_count - b.card_count) * dir;
        case 'updated':
        default:
          return (a.updated_at.getTime() - b.updated_at.getTime()) * dir;
      }
    });

    return result;
  }, [decks, filters]);

  // Count by status for tabs
  const counts = useMemo(() => {
    if (!decks) return { all: 0, brewing: 0, active: 0, archived: 0 };
    return {
      all: decks.length,
      brewing: decks.filter((d) => d.status === 'brewing').length,
      active: decks.filter((d) => d.status === 'active').length,
      archived: decks.filter((d) => d.status === 'archived').length,
    };
  }, [decks]);

  if (error) {
    return (
      <div className="p-8 text-center text-red-400">
        <p>Failed to load decks</p>
        <p className="text-sm text-zinc-500">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Decks</h1>
          {decks && decks.length > 0 && (
            <p className="mt-0.5 text-sm text-zinc-500">
              {filteredDecks.length} {filteredDecks.length === 1 ? 'deck' : 'decks'}
              {filteredDecks.length !== decks.length && ` of ${decks.length} total`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button icon={<FaPlus />} text="New Deck" onClick={() => setIsCreateModalOpen(true)} />
          <Button icon={<FaFileImport />} text="Import" color="secondary" onClick={() => setIsImportModalOpen(true)} />
          <Button icon={<FaFileExport />} text="Export All" color="secondary" onClick={() => setIsExportModalOpen(true)} />
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800/50 bg-zinc-900/30 px-6 py-3">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative max-w-75 min-w-50 flex-1">
            <FaSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-sm text-zinc-500" />
            <input
              type="text"
              placeholder="Search decks..."
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              className="w-full rounded-lg border border-zinc-700/50 bg-zinc-800/50 py-2 pr-3 pl-9 text-sm text-zinc-200 placeholder-zinc-500 focus:border-zinc-600 focus:outline-none"
            />
          </div>

          {/* Status tabs */}
          <Tabs
            tabs={VIEW_OPTIONS.map((opt) => ({ id: opt.value, label: opt.label, count: counts[opt.value] }))}
            activeId={filters.view}
            onChange={(view) => setFilters((f) => ({ ...f, view }))}
            variant="pill"
            label="Deck status"
          />
          {/* Format filter */}
          {availableFormats.length > 0 && (
            <select
              value={filters.format}
              onChange={(e) => setFilters((f) => ({ ...f, format: e.target.value as DeckFormat | 'all' }))}
              className="rounded-lg border border-zinc-700/50 bg-zinc-800/50 px-3 py-2 text-sm text-zinc-200 focus:border-zinc-600 focus:outline-none"
            >
              <option value="all">All Formats</option>
              {availableFormats.map((format) => (
                <option key={format} value={format}>
                  {DECK_FORMAT_LABELS[format]}
                </option>
              ))}
            </select>
          )}
        </div>
        {/* Sort */}
        <SortBySelector
          value={filters.sort}
          direction={filters.sortDirection}
          options={SORT_OPTIONS}
          variant="dropdown"
          onChange={(sort, sortDirection) => setFilters((f) => ({ ...f, sort, sortDirection }))}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : filteredDecks.length === 0 ? (
          <EmptyState
            title={filters.search || filters.view !== 'all' || filters.format !== 'all' ? 'No decks found' : 'No decks yet'}
            description={
              filters.search || filters.view !== 'all' || filters.format !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first deck to get started'
            }
            action={
              !filters.search && filters.view === 'all' && filters.format === 'all' ? (
                <Button text="Create Deck" icon={<FaPlus />} onClick={() => setIsCreateModalOpen(true)} />
              ) : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredDecks.map((deck) => {
              const coverFullCard = deck.cover_card_id ? getFullCard(deck.cover_card_id) : null;
              const coverMinimalCard = deck.cover_card_id ? getCard(deck.cover_card_id) : null;
              // MinimalCard images are pre-resolved at index build time; only full cards need face resolution
              const coverImageUris = coverFullCard ? getDisplayImageUris(coverFullCard) : (coverMinimalCard?.image_uris ?? null);
              const colorIdentity = deckColorIdentities.get(deck.id);
              return <DeckCard key={deck.id} deck={deck} coverImageUris={coverImageUris} colorIdentity={colorIdentity} />;
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateDeckModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />

      {/* Import Modal */}
      <ImportDeckModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onCreateDeck={handleCreateDeck} />

      {/* Preparing deck overlay */}
      {isPreparingDeck && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/80">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-zinc-300">Preparing deck…</p>
          </div>
        </div>
      )}

      {/* Export All Modal */}
      <ExportAllDecksModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} decks={decks ?? []} />
    </div>
  );
}
